import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, TextField, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Pagination, Modal,
    IconButton, Grid, Card, CardMedia, Tooltip, CircularProgress
} from '@mui/material';
import { CloudUpload as CloudUploadIcon, Close as CloseIcon, Visibility as VisibilityIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { GetAllEvents, CreateEvent, GetEventImages, DeleteEvent } from '../../../service/api/emsService/EventsService';
import dayjs from 'dayjs';
import { Toaster } from '../../../common/alertComponets/Toaster';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: 900,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    maxHeight: '90vh',
    overflowY: 'auto',
    borderRadius: 2
};

const EventConfig = () => {
    // State for List
    const [events, setEvents] = useState([]);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(5);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);

    // State for Form
    const [formData, setFormData] = useState({
        eventTitle: '',
        eventDescription: '',
        eventDate: dayjs().format('YYYY-MM-DD'),
        files: []
    });
    const [submitting, setSubmitting] = useState(false);

    // State for Image Modal
    const [openModal, setOpenModal] = useState(false);
    const [selectedImages, setSelectedImages] = useState([]);
    const [fetchingImages, setFetchingImages] = useState(false);

    const fetchEvents = useCallback(async () => {
        setLoading(true);
        try {
            const data = await GetAllEvents(page, size);
            if (data && data.content) {
                setEvents(data.content);
                setTotalPages(data.totalPages);
            }
        } catch (error) {
            console.error("Error fetching events:", error);
            Toaster('error', 'Failed to load events');
        } finally {
            setLoading(false);
        }
    }, [page, size]);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        setFormData(prev => ({ ...prev, files: Array.from(e.target.files) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.files.length === 0) {
            Toaster('warning', 'Please select at least one image');
            return;
        }

        setSubmitting(true);
        const submitData = new FormData();
        submitData.append('eventTitle', formData.eventTitle);
        submitData.append('eventDescription', formData.eventDescription);
        submitData.append('eventDate', formData.eventDate);
        formData.files.forEach(file => {
            submitData.append('files', file);
        });

        try {
            await CreateEvent(submitData);
            Toaster('success', 'Event created successfully');
            setFormData({
                eventTitle: '',
                eventDescription: '',
                eventDate: dayjs().format('YYYY-MM-DD'),
                files: []
            });
            setPage(0);
            fetchEvents();
        } catch (error) {
            console.error("Error creating event:", error);
            Toaster('error', 'Failed to create event');
        } finally {
            setSubmitting(false);
        }
    };

    const handleViewImages = async (eventId) => {
        setFetchingImages(true);
        setOpenModal(true);
        try {
            const images = await GetEventImages(eventId);
            setSelectedImages(images);
        } catch (error) {
            console.error("Error fetching images:", error);
            Toaster('error', 'Failed to fetch event images');
            setOpenModal(false);
        } finally {
            setFetchingImages(false);
        }
    };

    const handleDeleteEvent = async (eventId) => {
        if (window.confirm("Are you sure you want to delete this event?")) {
            try {
                await DeleteEvent(eventId);
                Toaster('success', 'Event deleted successfully');
                fetchEvents();
            } catch (error) {
                console.error("Error deleting event:", error);
                Toaster('error', 'Failed to delete event');
            }
        }
    };

    return (
        <Box p={4} sx={{ backgroundColor: '#f9f9fb', minHeight: '100vh' }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom color="primary" sx={{ mb: 4 }}>
                Event Configuration
            </Typography>

            <Grid container spacing={4}>
                {/* Form Section */}
                <Grid item xs={12} md={4}>
                    <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                            Add New Event
                        </Typography>
                        <form onSubmit={handleSubmit}>
                            <Box display="flex" flexDirection="column" gap={3}>
                                <TextField
                                    fullWidth
                                    label="Event Name"
                                    name="eventTitle"
                                    value={formData.eventTitle}
                                    onChange={handleFormChange}
                                    required
                                    variant="outlined"
                                />
                                <TextField
                                    fullWidth
                                    label="Event Date"
                                    name="eventDate"
                                    type="date"
                                    value={formData.eventDate}
                                    onChange={handleFormChange}
                                    InputLabelProps={{ shrink: true }}
                                    required
                                    variant="outlined"
                                />
                                <TextField
                                    fullWidth
                                    label="Event Description"
                                    name="eventDescription"
                                    value={formData.eventDescription}
                                    onChange={handleFormChange}
                                    multiline
                                    rows={4}
                                    variant="outlined"
                                />
                                <Box>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        startIcon={<CloudUploadIcon />}
                                        sx={{ borderRadius: 2, textTransform: 'none', py: 1 }}
                                    >
                                        Upload Event Photos
                                        <input
                                            type="file"
                                            hidden
                                            multiple
                                            accept="image/*"
                                            onChange={handleFileChange}
                                        />
                                    </Button>
                                    {formData.files.length > 0 && (
                                        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'green', fontWeight: 'medium' }}>
                                            {formData.files.length} images selected
                                        </Typography>
                                    )}
                                </Box>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={submitting}
                                    sx={{ mt: 2, py: 1.5, borderRadius: 2, fontWeight: 'bold' }}
                                >
                                    {submitting ? <CircularProgress size={24} color="inherit" /> : 'Create Event'}
                                </Button>
                            </Box>
                        </form>
                    </Paper>
                </Grid>

                {/* List Section */}
                <Grid item xs={12} md={8}>
                    <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                        <TableContainer sx={{ maxHeight: 600 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#412276', color: 'white' }}>Event Name</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#412276', color: 'white' }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#412276', color: 'white' }}>Description</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#412276', color: 'white' }} align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 10 }}>
                                                <CircularProgress />
                                            </TableCell>
                                        </TableRow>
                                    ) : events.length > 0 ? (
                                        events.map((event) => (
                                            <TableRow key={event.eventId} hover>
                                                <TableCell sx={{ fontWeight: 'medium' }}>{event.eventTitle}</TableCell>
                                                <TableCell>{dayjs(event.eventDate).format('MMM DD, YYYY')}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{
                                                        maxWidth: 300,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {event.eventDescription}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Box display="flex" justifyContent="center" gap={1}>
                                                        <Tooltip title="View Photos">
                                                            <IconButton
                                                                onClick={() => handleViewImages(event.eventId)}
                                                                color="secondary"
                                                                sx={{ backgroundColor: '#f0f4ff', '&:hover': { backgroundColor: '#e0eaff' } }}
                                                            >
                                                                <VisibilityIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete Event">
                                                            <IconButton
                                                                onClick={() => handleDeleteEvent(event.eventId)}
                                                                color="error"
                                                                sx={{ backgroundColor: '#fff0f0', '&:hover': { backgroundColor: '#ffdfdf' } }}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                                                No events found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box p={3} display="flex" justifyContent="center" bgcolor="white">
                            <Pagination
                                count={totalPages}
                                page={page + 1}
                                onChange={(e, v) => setPage(v - 1)}
                                color="primary"
                                size="large"
                            />
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Images Modal */}
            <Modal
                open={openModal}
                onClose={() => setOpenModal(false)}
                closeAfterTransition
            >
                <Box sx={modalStyle}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} borderBottom="1px solid #eee" pb={2}>
                        <Typography variant="h5" fontWeight="bold" color="primary">Event Gallery</Typography>
                        <IconButton onClick={() => setOpenModal(false)} size="large">
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {fetchingImages ? (
                        <Box display="flex" justifyContent="center" py={10}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Grid container spacing={3}>
                            {selectedImages.length > 0 ? (
                                selectedImages.map((imgBase64, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                        <Card elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                            <CardMedia
                                                component="img"
                                                height="200"
                                                image={`data:image/jpeg;base64,${imgBase64}`}
                                                alt={`Event photo ${index + 1}`}
                                                sx={{ objectFit: 'cover' }}
                                            />
                                        </Card>
                                    </Grid>
                                ))
                            ) : (
                                <Grid item xs={12}>
                                    <Typography align="center" color="textSecondary" sx={{ py: 4 }}>
                                        No photos available for this event.
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </Box>
            </Modal>
        </Box>
    );
};

export default EventConfig;
